import React, { useState, useRef, useEffect } from 'react';
import {
    View, StyleSheet, Text, TouchableOpacity, ScrollView,
    Image, Animated, Dimensions, Platform, TextInput, Alert
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Caption } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import { bookSession } from '../../api/mentor';
import { ActivityIndicator } from 'react-native';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const MENTOR_PRIMARY = '#7c3aed';

const SESSION_TYPES = [
    { id: 'one-on-one', label: '1-on-1 Session', icon: 'person-outline', duration: '30 min', color: '#7c3aed' },
    { id: 'group', label: 'Group Session', icon: 'people-outline', duration: '60 min', color: '#10B981' },
    { id: 'workshop', label: 'Workshop', icon: 'school-outline', duration: '90 min', color: '#F59E0B' },
    { id: 'quick-chat', label: 'Quick Chat', icon: 'chatbubble-outline', duration: '15 min', color: '#3B82F6' },
];

const TIME_SLOTS = [
    '9:00 AM', '10:00 AM', '11:00 AM',
    '2:00 PM', '3:00 PM', '4:00 PM',
    '5:00 PM', '6:00 PM',
];

const MentorBookingScreen = () => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const route = useRoute();
    const mentor = route.params?.mentor;

    const [selectedType, setSelectedType] = useState('one-on-one');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [message, setMessage] = useState('');
    const [step, setStep] = useState(1); // 1: type, 2: date/time, 3: message/confirm
    const [isBooking, setIsBooking] = useState(false);

    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
    }, []);

    // Generate next 7 days
    const getNextDays = () => {
        const days = [];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let i = 0; i < 7; i++) {
            const date = new Date();
            date.setDate(date.getDate() + i);
            days.push({
                id: i,
                day: dayNames[date.getDay()],
                date: date.getDate(),
                month: monthNames[date.getMonth()],
                full: date.toISOString().split('T')[0],
            });
        }
        return days;
    };

    const dates = getNextDays();

    const handleBookSession = async () => {
        try {
            setIsBooking(true);

            // Construct scheduledAt date
            // selectedDate.full is YYYY-MM-DD
            // selectedTime is "9:00 AM" etc
            
            // Basic parser for AM/PM
            const [time, modifier] = selectedTime.split(' ');
            let [hours, minutes] = time.split(':');
            if (hours === '12') hours = '00';
            if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
            
            const scheduledAt = new Date(`${selectedDate.full}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`);

            const durationMap = {
                'one-on-one': 30,
                'group': 60,
                'workshop': 90,
                'quick-chat': 15
            };

            await bookSession({
                mentorId: mentor.id,
                sessionType: selectedType,
                scheduledAt: scheduledAt.toISOString(),
                duration: durationMap[selectedType] || 30,
                message: message
            });

            Alert.alert(
                'Session Requested! 🎉',
                `Your ${selectedType} session with ${mentor?.name} has been requested for ${selectedDate?.month} ${selectedDate?.date} at ${selectedTime}. You'll receive a confirmation shortly.`,
                [{ text: 'Great!', onPress: () => navigation.goBack() }]
            );
        } catch (error) {
            console.error('Booking failed:', error);
            Alert.alert('Booking Failed', error || 'Something went wrong while requesting your session.');
        } finally {
            setIsBooking(false);
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return !!selectedType;
            case 2: return !!selectedDate && !!selectedTime;
            case 3: return true;
            default: return false;
        }
    };

    if (!mentor) {
        return (
            <View style={styles.container}>
                <Text>Mentor not found</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={[styles.headerWrapper, { paddingTop: insets.top }]}>
                <LinearGradient
                    colors={['#4c1d95', '#5b21b6', '#7c3aed']}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.headerDecorOrb} />

                <View style={styles.headerContent}>
                    <TouchableOpacity
                        style={styles.backBtn}
                        onPress={() => step > 1 ? setStep(step - 1) : navigation.goBack()}
                    >
                        <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                    </TouchableOpacity>

                    <View style={styles.headerCenter}>
                        <Text style={styles.headerTitle}>Book a Session</Text>
                        <Text style={styles.headerSubtitle}>with {mentor.name}</Text>
                    </View>

                    <TouchableOpacity
                        style={styles.closeBtn}
                        onPress={() => navigation.goBack()}
                    >
                        <Ionicons name="close" size={22} color="rgba(255,255,255,0.7)" />
                    </TouchableOpacity>
                </View>

                {/* Mentor mini card */}
                <View style={styles.mentorMiniCard}>
                    <Image
                        source={{ uri: mentor.profileImageUrl || 'https://i.pravatar.cc/150' }}
                        style={styles.miniAvatar}
                    />
                    <View style={styles.miniInfo}>
                        <Text style={styles.miniName}>{mentor.name}</Text>
                        <Text style={styles.miniType}>{mentor.mentorType || 'Mentor'}</Text>
                    </View>
                    <View style={styles.miniOnline}>
                        <View style={styles.miniOnlineDot} />
                        <Text style={styles.miniOnlineText}>Online</Text>
                    </View>
                </View>

                {/* Progress bar */}
                <View style={styles.progressBar}>
                    {[1, 2, 3].map((s) => (
                        <View
                            key={s}
                            style={[
                                styles.progressSegment,
                                s <= step && styles.progressSegmentActive
                            ]}
                        />
                    ))}
                </View>
            </View>

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 100 }]}
                showsVerticalScrollIndicator={false}
            >
                <Animated.View style={{ opacity: fadeAnim }}>
                    {/* Step 1: Session Type */}
                    {step === 1 && (
                        <View style={styles.stepContainer}>
                            <Text style={styles.stepTitle}>Choose Session Type</Text>
                            <Text style={styles.stepSubtitle}>Select the type of mentoring session you'd like</Text>

                            <View style={styles.sessionTypeGrid}>
                                {SESSION_TYPES.map((type) => (
                                    <TouchableOpacity
                                        key={type.id}
                                        style={[
                                            styles.sessionTypeCard,
                                            selectedType === type.id && styles.sessionTypeCardActive,
                                            selectedType === type.id && { borderColor: type.color }
                                        ]}
                                        onPress={() => setSelectedType(type.id)}
                                        activeOpacity={0.85}
                                    >
                                        <View style={[
                                            styles.sessionTypeIcon,
                                            { backgroundColor: type.color + '12' },
                                            selectedType === type.id && { backgroundColor: type.color + '20' }
                                        ]}>
                                            <Ionicons name={type.icon} size={26} color={type.color} />
                                        </View>
                                        <Text style={[
                                            styles.sessionTypeLabel,
                                            selectedType === type.id && { color: type.color }
                                        ]}>
                                            {type.label}
                                        </Text>
                                        <Text style={styles.sessionTypeDuration}>{type.duration}</Text>
                                        {selectedType === type.id && (
                                            <View style={[styles.sessionTypeCheck, { backgroundColor: type.color }]}>
                                                <Ionicons name="checkmark" size={14} color="#FFFFFF" />
                                            </View>
                                        )}
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>
                    )}

                    {/* Step 2: Date & Time */}
                    {step === 2 && (
                        <View style={styles.stepContainer}>
                            <Text style={styles.stepTitle}>Pick a Date & Time</Text>
                            <Text style={styles.stepSubtitle}>Choose when you'd like to meet</Text>

                            {/* Date Selection */}
                            <Text style={styles.fieldLabel}>Select Date</Text>
                            <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={styles.dateScroll}
                            >
                                {dates.map((d) => (
                                    <TouchableOpacity
                                        key={d.id}
                                        style={[
                                            styles.dateCard,
                                            selectedDate?.id === d.id && styles.dateCardActive,
                                        ]}
                                        onPress={() => setSelectedDate(d)}
                                    >
                                        <Text style={[
                                            styles.dateDay,
                                            selectedDate?.id === d.id && styles.dateDayActive
                                        ]}>
                                            {d.day}
                                        </Text>
                                        <Text style={[
                                            styles.dateNum,
                                            selectedDate?.id === d.id && styles.dateNumActive
                                        ]}>
                                            {d.date}
                                        </Text>
                                        <Text style={[
                                            styles.dateMonth,
                                            selectedDate?.id === d.id && styles.dateMonthActive
                                        ]}>
                                            {d.month}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </ScrollView>

                            {/* Time Selection */}
                            <Text style={[styles.fieldLabel, { marginTop: 24 }]}>Select Time</Text>
                            <View style={styles.timeGrid}>
                                {TIME_SLOTS.map((time) => (
                                    <TouchableOpacity
                                        key={time}
                                        style={[
                                            styles.timeSlot,
                                            selectedTime === time && styles.timeSlotActive,
                                        ]}
                                        onPress={() => setSelectedTime(time)}
                                    >
                                        <Text style={[
                                            styles.timeText,
                                            selectedTime === time && styles.timeTextActive
                                        ]}>
                                            {time}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>
                    )}

                    {/* Step 3: Message & Confirm */}
                    {step === 3 && (
                        <View style={styles.stepContainer}>
                            <Text style={styles.stepTitle}>Add a Message</Text>
                            <Text style={styles.stepSubtitle}>Let your mentor know what you'd like to discuss</Text>

                            <TextInput
                                style={styles.messageInput}
                                placeholder="Hi! I'd love to discuss..."
                                placeholderTextColor="#9CA3AF"
                                multiline
                                numberOfLines={5}
                                value={message}
                                onChangeText={setMessage}
                                textAlignVertical="top"
                            />

                            {/* Summary Card */}
                            <View style={styles.summaryCard}>
                                <Text style={styles.summaryTitle}>Session Summary</Text>

                                <View style={styles.summaryRow}>
                                    <Ionicons name="person-outline" size={16} color="#7c3aed" />
                                    <Text style={styles.summaryLabel}>Mentor</Text>
                                    <Text style={styles.summaryValue}>{mentor.name}</Text>
                                </View>
                                <View style={styles.summaryRow}>
                                    <Ionicons name="grid-outline" size={16} color="#10B981" />
                                    <Text style={styles.summaryLabel}>Type</Text>
                                    <Text style={styles.summaryValue}>
                                        {SESSION_TYPES.find(t => t.id === selectedType)?.label}
                                    </Text>
                                </View>
                                <View style={styles.summaryRow}>
                                    <Ionicons name="calendar-outline" size={16} color="#3B82F6" />
                                    <Text style={styles.summaryLabel}>Date</Text>
                                    <Text style={styles.summaryValue}>
                                        {selectedDate ? `${selectedDate.day}, ${selectedDate.month} ${selectedDate.date}` : '-'}
                                    </Text>
                                </View>
                                <View style={styles.summaryRow}>
                                    <Ionicons name="time-outline" size={16} color="#F59E0B" />
                                    <Text style={styles.summaryLabel}>Time</Text>
                                    <Text style={styles.summaryValue}>{selectedTime || '-'}</Text>
                                </View>
                                <View style={styles.summaryRow}>
                                    <Ionicons name="hourglass-outline" size={16} color="#EC4899" />
                                    <Text style={styles.summaryLabel}>Duration</Text>
                                    <Text style={styles.summaryValue}>
                                        {SESSION_TYPES.find(t => t.id === selectedType)?.duration}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    )}
                </Animated.View>
            </ScrollView>

            {/* Bottom Action */}
            <View style={[styles.bottomBar, { paddingBottom: insets.bottom + 8 }]}>
                <TouchableOpacity
                    style={[styles.actionBtn, !canProceed() && styles.actionBtnDisabled]}
                    activeOpacity={0.85}
                    disabled={!canProceed()}
                    onPress={() => {
                        if (step < 3) {
                            setStep(step + 1);
                        } else {
                            handleBookSession();
                        }
                    }}
                >
                    <LinearGradient
                        colors={canProceed() ? ['#7c3aed', '#6d28d9'] : ['#D1D5DB', '#D1D5DB']}
                        style={styles.actionBtnGradient}
                    >
                        <Text style={styles.actionBtnText}>
                            {isBooking ? 'Requesting...' : (step < 3 ? 'Continue' : 'Request Session')}
                        </Text>
                        {isBooking ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <Ionicons
                                name={step < 3 ? 'arrow-forward' : 'checkmark-circle'}
                                size={20}
                                color="#FFFFFF"
                            />
                        )}
                    </LinearGradient>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FAF5FF',
    },

    // Header
    headerWrapper: {
        overflow: 'hidden',
        paddingBottom: 18,
    },
    headerDecorOrb: {
        position: 'absolute',
        width: 150,
        height: 150,
        borderRadius: 75,
        backgroundColor: 'rgba(196,181,253,0.15)',
        right: -40,
        top: -20,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: Platform.OS === 'ios' ? 8 : 14,
    },
    backBtn: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerCenter: {
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    headerSubtitle: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.6)',
        fontWeight: '500',
    },
    closeBtn: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.1)',
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Mentor mini card
    mentorMiniCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginHorizontal: 16,
        marginTop: 14,
        backgroundColor: 'rgba(255,255,255,0.12)',
        borderRadius: 14,
        padding: 10,
    },
    miniAvatar: {
        width: 40,
        height: 40,
        borderRadius: 14,
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    miniInfo: {
        flex: 1,
    },
    miniName: {
        fontSize: 14,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    miniType: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.6)',
        fontWeight: '500',
    },
    miniOnline: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    miniOnlineDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#10B981',
    },
    miniOnlineText: {
        fontSize: 11,
        color: '#10B981',
        fontWeight: '600',
    },

    // Progress
    progressBar: {
        flexDirection: 'row',
        gap: 6,
        paddingHorizontal: 16,
        marginTop: 14,
    },
    progressSegment: {
        flex: 1,
        height: 4,
        borderRadius: 2,
        backgroundColor: 'rgba(255,255,255,0.2)',
    },
    progressSegmentActive: {
        backgroundColor: '#FFFFFF',
    },

    // Scroll
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingTop: 20,
    },

    // Steps
    stepContainer: {
        paddingHorizontal: 18,
    },
    stepTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 4,
    },
    stepSubtitle: {
        fontSize: 14,
        color: '#6B7280',
        marginBottom: 24,
    },

    // Session Types
    sessionTypeGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    sessionTypeCard: {
        width: (SCREEN_WIDTH - 48) / 2,
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: 18,
        alignItems: 'center',
        borderWidth: 2,
        borderColor: '#F3F4F6',
        ...getShadow('sm'),
        position: 'relative',
    },
    sessionTypeCardActive: {
        borderWidth: 2,
        backgroundColor: '#FEFBFF',
        ...getShadow('md'),
    },
    sessionTypeIcon: {
        width: 56,
        height: 56,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    sessionTypeLabel: {
        fontSize: 14,
        fontWeight: '700',
        color: '#374151',
        marginBottom: 4,
        textAlign: 'center',
    },
    sessionTypeDuration: {
        fontSize: 12,
        color: '#9CA3AF',
        fontWeight: '500',
    },
    sessionTypeCheck: {
        position: 'absolute',
        top: 10,
        right: 10,
        width: 24,
        height: 24,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Date
    fieldLabel: {
        fontSize: 15,
        fontWeight: '700',
        color: '#374151',
        marginBottom: 12,
    },
    dateScroll: {
        gap: 10,
        paddingRight: 18,
    },
    dateCard: {
        width: 70,
        alignItems: 'center',
        paddingVertical: 14,
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        borderWidth: 1.5,
        borderColor: '#F3F4F6',
        ...getShadow('sm'),
    },
    dateCardActive: {
        backgroundColor: MENTOR_PRIMARY,
        borderColor: MENTOR_PRIMARY,
        ...getShadow('md'),
    },
    dateDay: {
        fontSize: 12,
        fontWeight: '600',
        color: '#9CA3AF',
        marginBottom: 4,
    },
    dateDayActive: { color: 'rgba(255,255,255,0.7)' },
    dateNum: {
        fontSize: 22,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 2,
    },
    dateNumActive: { color: '#FFFFFF' },
    dateMonth: {
        fontSize: 11,
        fontWeight: '600',
        color: '#9CA3AF',
    },
    dateMonthActive: { color: 'rgba(255,255,255,0.7)' },

    // Time
    timeGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    timeSlot: {
        paddingHorizontal: 18,
        paddingVertical: 12,
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        borderWidth: 1.5,
        borderColor: '#F3F4F6',
        ...getShadow('sm'),
    },
    timeSlotActive: {
        backgroundColor: MENTOR_PRIMARY,
        borderColor: MENTOR_PRIMARY,
    },
    timeText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
    },
    timeTextActive: {
        color: '#FFFFFF',
    },

    // Message
    messageInput: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 16,
        fontSize: 14,
        color: '#111827',
        borderWidth: 1.5,
        borderColor: '#F3F4F6',
        minHeight: 120,
        marginBottom: 20,
        ...getShadow('sm'),
    },

    // Summary
    summaryCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: 18,
        ...getShadow('md'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    summaryTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 16,
    },
    summaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#F9FAFB',
    },
    summaryLabel: {
        flex: 1,
        fontSize: 13,
        fontWeight: '600',
        color: '#6B7280',
    },
    summaryValue: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111827',
    },

    // Bottom
    bottomBar: {
        paddingHorizontal: 18,
        paddingTop: 12,
        backgroundColor: 'rgba(250,245,255,0.95)',
        borderTopWidth: 1,
        borderTopColor: '#F3E8FF',
    },
    actionBtn: {
        borderRadius: 14,
        overflow: 'hidden',
        ...getShadow('lg'),
    },
    actionBtnDisabled: {
        opacity: 0.6,
    },
    actionBtnGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 16,
    },
    actionBtnText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
});

export default MentorBookingScreen;
